import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import type { PlateEditor as PlateEditorType, exportEditorToPdfServer as ExportEditorToPdfServerType } from '../PlateEditor';
import { docxBlobToPlateValue } from '../PlateEditor/serializers/docxBlobToPlateValue';
import { txtBlobToPlateValue } from '../PlateEditor/serializers/txtBlobToPlateValue';
import { useOutsideClick } from '../../utils/constants/hooks';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import WhiteCheckIcon from '../Reports/WhiteCheckIcon';
import viewerStyles from './DocumentViewer.module.scss';
import styles from './DocxDrawer.module.scss';

// `.docx` is a ZIP/OOXML archive (PK\x03\x04 magic bytes); legacy `.doc` is an
// OLE2 compound file (D0 CF 11 E0 A1 B1 1A E1) — docxBlobToPlateValue's mammoth
// pipeline can only read the former. Duplicated from DocxViewer.tsx (kept
// read-only + untouched per RECUR-26923) rather than extracted, since this is
// the only other call site.
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];
const OLE2_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

const hasMagic = (buffer: ArrayBuffer, magic: number[]): boolean => {
    if (buffer.byteLength < magic.length) return false;
    const bytes = new Uint8Array(buffer, 0, magic.length);
    return magic.every((b, i) => bytes[i] === b);
};

const TOOLBAR_HOST_ID = 'docx-drawer-toolbar-host';

export interface DocxDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Original file name; used as the drawer title and download filename. */
    fileName: string;
    /** Presigned URL of the .docx/.txt file to preview (Data Room path). */
    fileUrl?: string;
    /** Already-fetched blob (Ask AICA's auth-gated path). Takes priority over fileUrl. */
    blob?: Blob;
    /** Downloads the ORIGINAL file bytes (.docx or .txt, whichever fileName is). */
    onDownload: () => void;
    downloading?: boolean;
    /** Snapshot this docx into Reports (Ask AICA / Data Room view contexts).
     *  Becomes the PRIMARY header action when provided — gated by the caller's
     *  useAddToReportsVisible() check, mirroring SheetEditorDrawer. */
    onAddToReports?: () => void;
    /** Loading state for the Add to Reports action, controlled by the caller. */
    addingToReports?: boolean;
    /** Briefly true right after a successful add — swaps the button to a
     *  checkmark + "Added" state, controlled by the caller. */
    addedToReports?: boolean;
}

/**
 * Editable docx/txt drawer. Seeds a full Plate editor (with toolbar) from the
 * file via docxBlobToPlateValue (the same pipeline the Reports import uses)
 * or txtBlobToPlateValue for a `.txt` fileName — NOT the read-only DocxViewer
 * (that stays the static preview used elsewhere, e.g. Ask AICA). There is
 * deliberately no Save action here: edits are exploratory only and are never
 * written back to the original file — the header's Download ▾ offers a live
 * PDF export of the current editor content plus the ORIGINAL file bytes
 * (unaffected by edits), alongside the primary Add to Reports action.
 */
const DocxDrawer: React.FC<DocxDrawerProps> = ({
    open,
    onClose,
    fileName,
    fileUrl,
    blob,
    onDownload,
    downloading,
    onAddToReports,
    addingToReports,
    addedToReports,
}) => {
    // Deferred require (not a top-level static import): PlateEditor's toolbar
    // pulls ESM-only deps (@uiw/react-color, react-markdown's AI menu chain)
    // Jest can't parse. FileViewerDrawer imports DocxDrawer for every mime
    // type, so a static top-level import here would crash any suite that
    // merely imports FileViewerDrawer, even when it never renders this
    // component. require() inside the render body defers that chain until
    // DocxDrawer actually renders (the DOCX_TYPES branch) — DocxDrawer.test.tsx
    // still works because its own jest.mock('../PlateEditor', ...) intercepts
    // this require() the same way it would a static import.
    // exportEditorToPdfServer is pulled from the same barrel, so it rides the
    // same deferred require rather than opening a second static import path.
    const { PlateEditor, exportEditorToPdfServer } = require('../PlateEditor') as {
        PlateEditor: typeof PlateEditorType;
        exportEditorToPdfServer: typeof ExportEditorToPdfServerType;
    };

    // Derived from the original file name — drives converter selection (task 1)
    // and the extension-aware "Download as .<ext>" label, so a .txt file never
    // advertises ".docx" (and vice versa).
    const dotIndex = fileName.lastIndexOf('.');
    const fileExtension = dotIndex > -1 ? fileName.slice(dotIndex + 1).toLowerCase() : '';
    const fileNameWithoutExt = dotIndex > -1 ? fileName.slice(0, dotIndex) : fileName;
    const isTxtFile = fileExtension === 'txt';
    const originalDownloadLabel = fileExtension ? `Download as .${fileExtension}` : 'Download original file';

    const [initialValue, setInitialValue] = useState<any[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setInitialValue(null);

        const load = async () => {
            try {
                let source: Blob;
                if (blob) {
                    source = blob;
                } else if (fileUrl) {
                    const response = await fetch(fileUrl, { signal: controller.signal });
                    if (!response.ok) throw new Error('Failed to fetch file');
                    source = await response.blob();
                } else {
                    throw new Error('No document source provided');
                }

                let value: any[];
                if (isTxtFile) {
                    value = await txtBlobToPlateValue(source);
                } else {
                    const buffer = await source.arrayBuffer();
                    if (hasMagic(buffer, OLE2_MAGIC)) {
                        toast.error("Legacy .doc isn't supported — please re-save it as .docx and try again.");
                        throw new Error('Legacy .doc is not supported');
                    }
                    if (!hasMagic(buffer, ZIP_MAGIC)) {
                        throw new Error('Unrecognised document format');
                    }
                    value = await docxBlobToPlateValue(source);
                }

                if (isMounted) {
                    setInitialValue(value);
                    setLoading(false);
                }
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                console.error('[DocxDrawer] failed to load document', err instanceof Error ? err.message : 'unknown error');
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load document');
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [fileUrl, blob, isTxtFile]);

    const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    const downloadMenuRef = useRef<HTMLDivElement>(null);
    useOutsideClick(downloadMenuRef, () => setDownloadMenuOpen(false));

    // True once the editor has actually mounted with content — gates both the
    // PlateEditor render below and the "Download as .pdf" item (exporting
    // before then would export the loading/error box instead of the doc).
    const editorReady = !loading && !error && initialValue != null;
    const canvasRef = useRef<HTMLDivElement>(null);
    const [exportingPdf, setExportingPdf] = useState(false);

    // PDF export reads the live contenteditable DOM (via canvasRef), not a
    // tracked onChange value — so it always reflects in-session edits with no
    // extra wiring, same as PlateDocumentDrawer/HiddenPdfExporter's own
    // "Export .pdf" path.
    const handleDownloadPdf = async () => {
        if (!canvasRef.current || exportingPdf) return;
        setExportingPdf(true);
        try {
            await exportEditorToPdfServer({ target: canvasRef.current, filename: fileNameWithoutExt });
        } catch {
            toast.error('PDF export failed. Please try again.');
        } finally {
            setExportingPdf(false);
        }
    };

    const headerRight = (
        <div className={styles.actions}>
            <div className={styles.downloadGroup} ref={downloadMenuRef}>
                <button
                    type="button"
                    className={styles.downloadBtn}
                    onClick={() => setDownloadMenuOpen((o) => !o)}
                    aria-haspopup="true"
                    aria-expanded={downloadMenuOpen}
                    disabled={downloading}
                >
                    Download <span className={styles.downloadCaret}>▾</span>
                </button>
                {downloadMenuOpen && (
                    <div className={styles.downloadPopover} role="menu" aria-label="Download options">
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.downloadPopoverItem}
                            onClick={() => {
                                setDownloadMenuOpen(false);
                                handleDownloadPdf();
                            }}
                            disabled={!editorReady || exportingPdf}
                        >
                            Download as .pdf
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.downloadPopoverItem}
                            onClick={() => {
                                setDownloadMenuOpen(false);
                                onDownload();
                            }}
                        >
                            {originalDownloadLabel}
                        </button>
                    </div>
                )}
            </div>
            {onAddToReports && (
                <Button
                    type="button"
                    text={addedToReports ? <WhiteCheckIcon /> : 'Add to Reports'}
                    variant="purple"
                    className={styles.headerBtn}
                    onClick={addedToReports ? () => {} : onAddToReports}
                    disabled={addingToReports}
                    loading={!addedToReports && addingToReports}
                />
            )}
        </div>
    );

    return (
        <BottomDrawer
            open={open}
            onClose={onClose}
            title={fileName}
            headerRight={headerRight}
            className={styles.drawer}
            bodyClassName={styles.body}
            height="100vh"
        >
            <div className={styles.toolbarHost} id={TOOLBAR_HOST_ID} />
            <div className={styles.viewerWrapper} ref={canvasRef}>
                {loading && (
                    <div className={viewerStyles.loadingBox}>
                        <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                    </div>
                )}
                {!loading && !editorReady && (
                    <div className={viewerStyles.errorBox}>
                        <p className={viewerStyles.errorText}>Unable to open this document for editing</p>
                    </div>
                )}
                {editorReady && (
                    <PlateEditor
                        key={fileUrl ?? fileName}
                        initialValue={initialValue}
                        toolbarHostId={TOOLBAR_HOST_ID}
                        className={styles.editor}
                        fullHeight
                    />
                )}
            </div>
        </BottomDrawer>
    );
};

export default DocxDrawer;
